"use client";
/* eslint-disable @next/next/no-img-element */

import { ChangeEvent, FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  FeedbackClientInput,
  FeedbackWidgetPosition,
  FeedbackWidgetProps,
  feedbackClientSchema,
} from "@/shared/feedback";
import { resolveStableWidgetAuthBaseUrl } from "@/lib/widget-auth-url";
import { getDeviceInfo } from "./lib/device";
import { sanitizeInput } from "./lib/sanitize";
import { WIDGET_BASE_CSS } from "./widget-styles";
import { PEEQBACK_ICON_DATA_URI } from "@/lib/peeqback-icon";

const DEFAULT_PROPS: Required<FeedbackWidgetProps> = {
  brandColor: "#2563eb",
  position: "bottom-right",
  logoUrl: PEEQBACK_ICON_DATA_URI,
  hideBranding: false,
  title: "Share feedback",
  buttonText: "Feedback",
  buttonStyle: "icon-text",
  successMessage: "Thanks for your feedback.",
  apiEndpoint: "/api/widget/feedback",
  roadmapUrl: "/roadmap",
  workspaceKey: "",
  submissionsEnabled: true,
  allowGuestSubmissions: true,
  showRoadmapLink: true,
  openSignal: 0,
  user: {},
};

type Props = FeedbackWidgetProps & {
  injectStyles?: boolean;
};

type WidgetStatus = "idle" | "loading" | "success" | "error";
type FormState = Omit<FeedbackClientInput, "rating" | "name" | "email"> & {
  rating: number;
  name: string;
  email: string;
};

type FlowType = "selection" | "feature_request" | "bug" | "general";

type SimilarRequest = {
  id: string;
  title: string;
  votes: number;
  status: string;
};

type UpvotedState = {
  request: SimilarRequest;
  newVotes: number;
};

type WidgetAuthResult = {
  workspaceKey: string;
  origin?: string;
  auth: {
    token: string;
    expiresAt: string;
  };
  identity: {
    id: string;
    name: string;
    email: string;
  };
};

type SubmissionOverrides = {
  widgetAuthToken?: string;
  authenticatedUser?: { id: string; name: string; email: string } | null;
};

type PersistedWidgetAuthSession = WidgetAuthResult & {
  savedAt: number;
};

const positionClassMap: Record<FeedbackWidgetPosition, string> = {
  "bottom-right": "pfb-position-bottom-right",
  "bottom-left": "pfb-position-bottom-left",
  "top-right": "pfb-position-top-right",
  "top-left": "pfb-position-top-left",
};

const flowTypeLabel: Record<FlowType, string> = {
  selection: "",
  feature_request: "Feature Request",
  bug: "Bug Report",
  general: "General Feedback",
};

type PresignUploadResponse = {
  uploadUrl: string;
  publicUrl: string;
  method?: string;
  headers?: Record<string, string>;
};

const MAX_UPLOAD_SIZE_BYTES = 2_000_000;
const MAX_INPUT_IMAGE_SIZE_BYTES = 8_000_000;
const MAX_SCREENSHOT_DIMENSION = 1920;

function replaceExtension(filename: string, nextExtension: string): string {
  if (!/\.[a-z0-9]+$/i.test(filename)) {
    return `${filename}${nextExtension}`;
  }
  return filename.replace(/\.[a-z0-9]+$/i, nextExtension);
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  contentType = "image/webp",
  quality = 0.82
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      contentType,
      quality
    );
  });
}

async function optimizeImageForUpload(
  source: File | Blob,
  filename: string,
  maxDimension: number
): Promise<{ file: File; contentType: string }> {
  const sourceType = source.type || "application/octet-stream";
  if (!sourceType.startsWith("image/") || sourceType === "image/svg+xml" || sourceType === "image/gif") {
    return {
      file:
        source instanceof File
          ? source
          : new File([source], filename, { type: sourceType }),
      contentType: sourceType,
    };
  }

  const imageUrl = URL.createObjectURL(source);
  const image = new Image();
  image.decoding = "async";

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Could not decode image."));
    image.src = imageUrl;
  }).finally(() => {
    URL.revokeObjectURL(imageUrl);
  });

  const longestEdge = Math.max(image.width, image.height);
  const scale = longestEdge > maxDimension ? maxDimension / longestEdge : 1;
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    return {
      file:
        source instanceof File
          ? source
          : new File([source], filename, { type: sourceType }),
      contentType: sourceType,
    };
  }
  context.drawImage(image, 0, 0, width, height);

  const optimizedBlob = await canvasToBlob(canvas, "image/webp", 0.82);
  if (optimizedBlob.size === 0 || optimizedBlob.size >= source.size) {
    return {
      file:
        source instanceof File
          ? source
          : new File([source], filename, { type: sourceType }),
      contentType: sourceType,
    };
  }

  return {
    file: new File([optimizedBlob], replaceExtension(filename, ".webp"), {
      type: "image/webp",
    }),
    contentType: "image/webp",
  };
}

async function requestPresignedUpload(
  file: File,
  apiEndpoint: string,
  options?: {
    workspaceKey?: string;
    widgetAuthToken?: string;
  }
): Promise<PresignUploadResponse> {
  const res = await fetch(`${apiEndpoint.replace(/\/feedback$/, "")}/upload/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type,
      size: file.size,
      workspaceKey: options?.workspaceKey || "",
      widgetAuthToken: options?.widgetAuthToken || "",
    }),
  });
  if (!res.ok) {
    await res.json().catch(() => ({}));
    throw new Error("Failed to create upload URL.");
  }

  return (await res.json()) as PresignUploadResponse;
}

async function uploadViaServerFallback(
  file: File,
  apiEndpoint: string,
  options?: {
    workspaceKey?: string;
    widgetAuthToken?: string;
  }
): Promise<string> {
  const body = new FormData();
  body.append("file", file, file.name);
  if (options?.workspaceKey) {
    body.append("workspaceKey", options.workspaceKey);
  }
  if (options?.widgetAuthToken) {
    body.append("widgetAuthToken", options.widgetAuthToken);
  }
  const res = await fetch(`${apiEndpoint.replace(/\/feedback$/, "")}/upload/presign`, {
    method: "POST",
    body,
  });
  if (!res.ok) {
    await res.json().catch(() => ({}));
    throw new Error("Failed to upload");
  }
  const { publicUrl } = await res.json();
  return publicUrl;
}

async function uploadToR2(
  file: File | Blob,
  filename: string,
  apiEndpoint: string,
  options?: {
    workspaceKey?: string;
    widgetAuthToken?: string;
  }
): Promise<string> {
  const optimized = await optimizeImageForUpload(file, filename, MAX_SCREENSHOT_DIMENSION);
  if (optimized.file.size > MAX_UPLOAD_SIZE_BYTES) {
    throw new Error("Image is too large after optimization. Keep it under 2MB.");
  }

  try {
    const presign = await requestPresignedUpload(
      optimized.file,
      apiEndpoint,
      options
    );
    const uploadResponse = await fetch(presign.uploadUrl, {
      method: presign.method || "PUT",
      headers: presign.headers || { "Content-Type": optimized.contentType },
      body: optimized.file,
    });
    if (!uploadResponse.ok) {
      throw new Error("Direct upload failed.");
    }
    return presign.publicUrl;
  } catch {
    // Keep widget functional if bucket CORS for direct PUT is not configured yet.
    return uploadViaServerFallback(optimized.file, apiEndpoint, options);
  }
}

async function captureScreenShotBlob(): Promise<Blob> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: false,
  });

  try {
    const video = document.createElement("video");
    video.srcObject = stream;

    await video.play();

    const maxVideoEdge = Math.max(video.videoWidth, video.videoHeight);
    const scale = maxVideoEdge > MAX_SCREENSHOT_DIMENSION ? MAX_SCREENSHOT_DIMENSION / maxVideoEdge : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Cannot capture screenshot.");
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvasToBlob(canvas, "image/webp", 0.82);
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

function getInitialForm(type: FlowType): FormState {
  return {
    title: "",
    name: "",
    email: "",
    type: type === "selection" ? "general" : type,
    rating: 5,
    message: "",
    screenshotUrl: "",
    ...getDeviceInfo(),
  };
}

function getWidgetAuthSessionStorageKey(workspaceKey: string, origin: string): string {
  return `peeqback_widget_auth_session:${workspaceKey}:${origin}`;
}

function isWidgetAuthResultValid(
  result: WidgetAuthResult | PersistedWidgetAuthSession,
  expectedWorkspaceKey?: string,
  expectedOrigin?: string
): boolean {
  if (!result?.auth?.token || !result?.auth?.expiresAt) {
    return false;
  }

  const expiresAt = Date.parse(result.auth.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    return false;
  }

  if (expectedWorkspaceKey && result.workspaceKey !== expectedWorkspaceKey) {
    return false;
  }

  if (expectedOrigin && result.origin && result.origin !== expectedOrigin) {
    return false;
  }

  return true;
}

export function FeedbackWidget(rawProps: Props) {
  const props = useMemo(() => ({ ...DEFAULT_PROPS, ...rawProps }), [rawProps]);
  const widgetLogoUrl = (props.logoUrl || "").trim() || PEEQBACK_ICON_DATA_URI;
  const hasInteractiveContent = props.submissionsEnabled || props.showRoadmapLink;

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<WidgetStatus>("idle");
  const [error, setError] = useState("");
  const [flowType, setFlowType] = useState<FlowType>("selection");
  const [formData, setFormData] = useState<FormState>(getInitialForm("selection"));
  const [similarRequests, setSimilarRequests] = useState<SimilarRequest[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [upvotedState, setUpvotedState] = useState<UpvotedState | null>(null);
  const [upvoteComment, setUpvoteComment] = useState("");
  const [upvoteLoading, setUpvoteLoading] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const [pendingScreenshot, setPendingScreenshot] = useState<{ blob: Blob; filename: string } | null>(null);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [widgetAuthToken, setWidgetAuthToken] = useState("");
  const [authenticatedUser, setAuthenticatedUser] = useState<{ id: string; name: string; email: string } | null>(null);

  const dialogId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);

  const apiBase = useMemo(
    () => props.apiEndpoint.replace(/\/feedback$/, ""),
    [props.apiEndpoint]
  );

  const authOrigin = useMemo(() => {
    try {
      // Derive auth origin from the API endpoint (lives on the app server),
      // not window.location (which is the host page embedding the widget).
      if (props.apiEndpoint) {
        const apiUrl = new URL(props.apiEndpoint, window.location.href);
        return resolveStableWidgetAuthBaseUrl(apiUrl.host);
      }
      if (typeof window !== "undefined") {
        return resolveStableWidgetAuthBaseUrl(window.location.host);
      }
      return resolveStableWidgetAuthBaseUrl("localhost:3000");
    } catch {
      return typeof window !== "undefined"
        ? resolveStableWidgetAuthBaseUrl(window.location.host)
        : "";
    }
  }, [props.apiEndpoint]);

  const widgetRequestOrigin = useMemo(
    () => (typeof window !== "undefined" ? window.location.origin : ""),
    []
  );

  const persistedAuthStorageKey = useMemo(() => {
    const workspaceKey = props.workspaceKey?.trim();
    if (!workspaceKey || !widgetRequestOrigin) {
      return "";
    }

    return getWidgetAuthSessionStorageKey(workspaceKey, widgetRequestOrigin);
  }, [props.workspaceKey, widgetRequestOrigin]);

  const clearPersistedWidgetAuthSession = useCallback(() => {
    if (!persistedAuthStorageKey || typeof window === "undefined") {
      return;
    }

    try {
      localStorage.removeItem(persistedAuthStorageKey);
    } catch {
      // ignore storage cleanup failures
    }
  }, [persistedAuthStorageKey]);

  const persistWidgetAuthSession = useCallback(
    (result: WidgetAuthResult) => {
      if (!persistedAuthStorageKey || typeof window === "undefined") {
        return;
      }

      try {
        const payload: PersistedWidgetAuthSession = {
          ...result,
          savedAt: Date.now(),
        };
        localStorage.setItem(persistedAuthStorageKey, JSON.stringify(payload));
      } catch {
        // ignore storage persistence failures
      }
    },
    [persistedAuthStorageKey]
  );

  useEffect(() => {
    if (!persistedAuthStorageKey || widgetAuthToken || typeof window === "undefined") {
      return;
    }

    try {
      const raw = localStorage.getItem(persistedAuthStorageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as PersistedWidgetAuthSession;
      if (
        !isWidgetAuthResultValid(
          parsed,
          props.workspaceKey?.trim() || "",
          widgetRequestOrigin
        )
      ) {
        localStorage.removeItem(persistedAuthStorageKey);
        return;
      }

      setWidgetAuthToken(parsed.auth.token);
      setAuthenticatedUser(parsed.identity);
    } catch {
      // ignore restore failures
    }
  }, [persistedAuthStorageKey, props.workspaceKey, widgetAuthToken, widgetRequestOrigin]);

  // Primary: postMessage from the Google sign-in popup
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (authOrigin && event.origin !== authOrigin) return;
      if (
        event.data &&
        event.data.type === "PEEQBACK_AUTH_SUCCESS" &&
        event.data.result?.auth?.token
      ) {
        const result = event.data.result as WidgetAuthResult;
        persistWidgetAuthSession(result);
        setWidgetAuthToken(result.auth.token);
        setAuthenticatedUser(result.identity);
        setShowAuthGate(false);
        try {
          localStorage.removeItem("peeqback_widget_auth");
        } catch {
          // ignore storage cleanup failures
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [authOrigin, persistWidgetAuthSession]);

  // Fallback: poll localStorage when the auth gate is open (handles nulled window.opener after OAuth)
  useEffect(() => {
    if (!showAuthGate) return;
    const interval = setInterval(() => {
      try {
        const raw = localStorage.getItem("peeqback_widget_auth");
        if (!raw) return;
        const { result, ts } = JSON.parse(raw) as {
          result: WidgetAuthResult;
          ts: number;
        };
        if (
          Date.now() - ts < 300_000 &&
          isWidgetAuthResultValid(
            result,
            props.workspaceKey?.trim() || "",
            widgetRequestOrigin
          )
        ) {
          persistWidgetAuthSession(result);
          setWidgetAuthToken(result.auth.token);
          setAuthenticatedUser(result.identity);
          setShowAuthGate(false);
          localStorage.removeItem("peeqback_widget_auth");
        }
      } catch {
        // ignore storage polling failures
      }
    }, 500);
    return () => clearInterval(interval);
  }, [persistWidgetAuthSession, props.workspaceKey, showAuthGate, widgetRequestOrigin]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (open && flowType !== "selection") {
      titleInputRef.current?.focus();
    }
  }, [open, flowType]);

  useEffect(() => {
    if (!props.openSignal) {
      return;
    }

    setOpen(true);
    setStatus("idle");
    setError("");
    setFlowType("selection");
    setFormData(getInitialForm("selection"));
    setSimilarRequests([]);
    setUpvotedState(null);
    setUpvoteComment("");
  }, [props.openSignal]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (upvotedState) {
          setUpvotedState(null);
          setUpvoteComment("");
          return;
        }
        if (flowType !== "selection") {
          setFlowType("selection");
          setFormData(getInitialForm("selection"));
          setSimilarRequests([]);
          setStatus("idle");
          setError("");
          return;
        }
        setOpen(false);
      }
    };

    const onClickOutside = (event: MouseEvent) => {
      // Use composedPath() so clicks inside the shadow DOM are recognised
      const path = event.composedPath();
      if (rootRef.current && !path.includes(rootRef.current)) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open, flowType, upvotedState]);

  const setValue = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const openWidgetAuthPopup = () => {
    if (!authOrigin) {
      setError("Sign-in is temporarily unavailable.");
      return;
    }

    if (!props.workspaceKey) {
      setError("Widget is missing a workspace key.");
      return;
    }

    setError("");

    const authPopupUrl = new URL(`${authOrigin}/widget-auth`);
    if (props.workspaceKey) {
      authPopupUrl.searchParams.set("workspaceKey", props.workspaceKey);
    }
    if (typeof window !== "undefined") {
      authPopupUrl.searchParams.set("origin", window.location.origin);
    }

    const popup = window.open(
      authPopupUrl.toString(),
      "peeqback-auth",
      "width=480,height=580,left=" +
        Math.round(window.screenX + (window.outerWidth - 480) / 2) +
        ",top=" +
        Math.round(window.screenY + (window.outerHeight - 580) / 2) +
        ",toolbar=no,menubar=no,scrollbars=no"
    );

    if (!popup) {
      setError("Popup was blocked. Please allow popups and try again.");
    }
  };

  const openFlow = (nextType: FlowType) => {
    setStatus("idle");
    setError("");
    setAuthNotice("");
    setFlowType(nextType);
    setFormData(getInitialForm(nextType));
    setSimilarRequests([]);
    setUpvotedState(null);
    setUpvoteComment("");
  };

  const buildSubmissionPayload = useCallback((overrides?: SubmissionOverrides) => {
    const resolvedUser = overrides?.authenticatedUser ?? authenticatedUser ?? props.user ?? {};
    const sanitized: FeedbackClientInput = {
      ...formData,
      // screenshotUrl is a blob: preview URL at this point — clear it here
      // since the real R2 URL is set later in submitParsedFeedback
      screenshotUrl: "",
      title: sanitizeInput(formData.title || ""),
      name: sanitizeInput((resolvedUser as { name?: string }).name || ""),
      email: sanitizeInput((resolvedUser as { email?: string }).email || ""),
      type: flowType === "selection" ? "general" : flowType,
      rating: 5,
      message: sanitizeInput(formData.message || ""),
      screenResolution: sanitizeInput(formData.screenResolution || ""),
      language: sanitizeInput(formData.language || ""),
      timezone: sanitizeInput(formData.timezone || ""),
      pageUrl: sanitizeInput(formData.pageUrl || ""),
      identifiedUserId: sanitizeInput(resolvedUser.id || ""),
    };

    return feedbackClientSchema.safeParse(sanitized);
  }, [authenticatedUser, props.user, formData, flowType]);

  const setScreenshotBlob = useCallback((blob: Blob, filename: string) => {
    // Revoke previous preview URL to avoid memory leaks
    if (screenshotPreviewUrl) {
      URL.revokeObjectURL(screenshotPreviewUrl);
    }
    setPendingScreenshot({ blob, filename });
    const previewUrl = URL.createObjectURL(blob);
    setScreenshotPreviewUrl(previewUrl);
    setValue("screenshotUrl", previewUrl);
    setError("");
  }, [screenshotPreviewUrl]);

  const clearScreenshot = useCallback(() => {
    if (screenshotPreviewUrl) {
      URL.revokeObjectURL(screenshotPreviewUrl);
    }
    setPendingScreenshot(null);
    setScreenshotPreviewUrl("");
    setValue("screenshotUrl", "");
  }, [screenshotPreviewUrl]);

  const submitParsedFeedback = useCallback(async (
    parsed: ReturnType<typeof feedbackClientSchema.safeParse>,
    overrides?: SubmissionOverrides
  ) => {
    if (!parsed.success) {
      setStatus("error");
      setError(parsed.error.issues[0]?.message || "Validation failed.");
      return false;
    }

    setStatus("loading");
    setError("");

    try {
      if (!props.workspaceKey) {
        setAuthNotice("");
        setStatus("error");
        setError("Widget is missing a workspace key.");
        return false;
      }

      // Upload pending screenshot to R2 at submit time; skip on failure
      let screenshotUrl = parsed.data.screenshotUrl || "";
      if (pendingScreenshot) {
        try {
          screenshotUrl = await uploadToR2(
            pendingScreenshot.blob,
            pendingScreenshot.filename,
            props.apiEndpoint,
            {
              workspaceKey: props.workspaceKey,
              widgetAuthToken: overrides?.widgetAuthToken ?? widgetAuthToken,
            }
          );
        } catch {
          // R2 upload failed — submit without the screenshot
          screenshotUrl = "";
        }
      }

      const response = await fetch(props.apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          {
            ...parsed.data,
            screenshotUrl,
            workspaceKey: props.workspaceKey,
            widgetAuthToken: overrides?.widgetAuthToken ?? widgetAuthToken,
          }
        ),
      });

      if (!response.ok) {
        await response.json().catch(() => ({}));
        if (response.status === 401 && !props.allowGuestSubmissions) {
          clearPersistedWidgetAuthSession();
          setWidgetAuthToken("");
          setAuthenticatedUser(null);
          setPendingAutoSubmit(true);
          setShowAuthGate(true);
          setStatus("idle");
          setError("Your sign-in session expired. Sign in again and we'll submit your feedback.");
          return false;
        }
        throw new Error("Failed to submit feedback.");
      }

      setAuthNotice("");
      setStatus("success");
      setFormData(getInitialForm(flowType));
      clearScreenshot();
      return true;
    } catch {
      setAuthNotice("");
      setStatus("error");
      setError("Something went wrong while sending feedback.");
      return false;
    }
  }, [
    clearPersistedWidgetAuthSession,
    clearScreenshot,
    flowType,
    pendingScreenshot,
    props.allowGuestSubmissions,
    props.apiEndpoint,
    props.workspaceKey,
    widgetAuthToken,
  ]);

  useEffect(() => {
    if (!pendingAutoSubmit || !widgetAuthToken) {
      return;
    }

    const parsed = buildSubmissionPayload({
      widgetAuthToken,
      authenticatedUser,
    });

    setPendingAutoSubmit(false);
    setShowAuthGate(false);
    setAuthNotice("Signed in. Submitting your saved feedback...");

    void submitParsedFeedback(parsed, {
      widgetAuthToken,
      authenticatedUser,
    });
  }, [authenticatedUser, buildSubmissionPayload, pendingAutoSubmit, submitParsedFeedback, widgetAuthToken]);

  const searchSimilar = (query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchAbortRef.current?.abort();

    const trimmed = query.trim();
    if (trimmed.length < 3 || !props.workspaceKey) {
      setSimilarRequests([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const params = new URLSearchParams({ q: trimmed });
        params.set("workspaceKey", props.workspaceKey);
        const res = await fetch(`${apiBase}/search?${params}`, {
          signal: controller.signal,
        });
        if (res.ok && !controller.signal.aborted) {
          const data = await res.json();
          setSimilarRequests(data.results || []);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    }, 350);
  };

  const handleUpvote = async (request: SimilarRequest) => {
    if (!props.workspaceKey) {
      setError("Widget is missing a workspace key.");
      return;
    }
    setUpvoteLoading(true);
    try {
      const res = await fetch(`${apiBase}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemId: request.id,
          workspaceKey: props.workspaceKey,
          widgetAuthToken,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setUpvotedState({ request, newVotes: data.vote?.votes ?? request.votes + 1 });
        setSimilarRequests([]);
      }
    } catch {
      setError("Could not register your vote. Please try again.");
    } finally {
      setUpvoteLoading(false);
    }
  };

  const handleUpvoteCommentSubmit = async () => {
    if (!upvotedState || !upvoteComment.trim()) {
      setUpvotedState(null);
      setUpvoteComment("");
      setStatus("success");
      return;
    }
    setUpvoteLoading(true);
    try {
      if (!props.workspaceKey) {
        throw new Error("Missing workspace key");
      }
      await fetch(props.apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: upvotedState.request.title,
          type: flowType === "selection" ? "general" : flowType,
          rating: 5,
          message: upvoteComment.trim(),
          workspaceKey: props.workspaceKey,
          widgetAuthToken,
          identifiedUserId: props.user?.id || "",
          ...getDeviceInfo(),
        }),
      });
    } catch {
      // Comment is optional, don't block success
    } finally {
      setUpvoteLoading(false);
      setUpvotedState(null);
      setUpvoteComment("");
      setStatus("success");
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      clearScreenshot();
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Image only.");
      return;
    }

    if (file.size > MAX_INPUT_IMAGE_SIZE_BYTES) {
      setError("Max input size is 8MB.");
      return;
    }

    setScreenshotBlob(file, file.name);
  };

  const handleTakeScreenshot = async () => {
    try {
      const blob = await captureScreenShotBlob();
      setScreenshotBlob(blob, "screenshot.webp");
    } catch (captureError) {
      setError(
        "Screen capture failed or was cancelled."
      );
    }
  };

  const submitFeedback = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!props.submissionsEnabled) {
      setStatus("error");
      setError("Feedback submissions are currently closed for this workspace.");
      return;
    }

    if (!props.workspaceKey) {
      setStatus("error");
      setError("Widget is missing a workspace key.");
      return;
    }

    const parsed = buildSubmissionPayload();
    if (!parsed.success) {
      setStatus("error");
      setError(parsed.error.issues[0]?.message || "Validation failed.");
      return;
    }

    if (!props.allowGuestSubmissions && !widgetAuthToken) {
      setPendingAutoSubmit(true);
      setShowAuthGate(true);
      setStatus("idle");
      setError("");
      setAuthNotice("");
      return;
    }

    await submitParsedFeedback(parsed);
  };

  if (!hasInteractiveContent) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={`pfb-root ${positionClassMap[props.position]}`}
      style={{ ["--pfb-brand" as string]: props.brandColor }}
    >
      {rawProps.injectStyles !== false ? (
        <style suppressHydrationWarning>{WIDGET_BASE_CSS}</style>
      ) : null}

      {open ? (
        <section
          id={dialogId}
          className="pfb-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${dialogId}-title`}
        >
          <header className="pfb-header">
            <div className="pfb-header-title-wrap">
              <img src={widgetLogoUrl} alt="" className="pfb-logo" />
              <div>
                <h2 id={`${dialogId}-title`} className="pfb-title">
                  {flowType === "selection" ? props.title : flowTypeLabel[flowType]}
                </h2>
                <p className="pfb-subtitle">Help us build what users actually love.</p>
              </div>
            </div>
            <button type="button" className="pfb-close" aria-label="Close" onClick={() => setOpen(false)}>
              ×
            </button>
          </header>

          {showAuthGate ? (
            <div className="pfb-auth-gate">
              <div className="pfb-auth-gate-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="14" width="20" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M10 14v-4a6 6 0 1 1 12 0v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <circle cx="16" cy="21" r="1.5" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="pfb-auth-gate-title">Sign in to continue</h3>
              <p className="pfb-auth-gate-body">
                {pendingAutoSubmit
                  ? "We saved your feedback. Sign in and we'll submit it automatically."
                  : "This workspace requires a verified identity before submitting feedback."}
              </p>
              {error ? (
                <div className="pfb-error" role="alert" style={{ marginBottom: "12px" }}>
                  {error}
                </div>
              ) : null}
              <button
                type="button"
                className="pfb-btn pfb-btn-google"
                onClick={openWidgetAuthPopup}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18L12.048 13.56C11.24 14.1 10.211 14.42 9 14.42c-2.392 0-4.415-1.615-5.139-3.786H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
                  <path d="M3.861 10.634A5.408 5.408 0 0 1 3.579 9c0-.566.097-1.116.282-1.634V5.034H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.034l2.904-2.4Z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 5.034l2.904 2.332C4.585 5.195 6.608 3.58 9 3.58Z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </button>
              <button
                type="button"
                className="pfb-btn pfb-btn-secondary"
                style={{ marginTop: "8px" }}
                onClick={() => {
                  setShowAuthGate(false);
                  setPendingAutoSubmit(false);
                }}
              >
                Back
              </button>
            </div>
          ) : flowType === "selection" ? (
            <div className="pfb-selection-grid">
              <button
                type="button"
                className="pfb-card"
                onClick={() => openFlow("feature_request")}
                disabled={!props.submissionsEnabled}
              >
                <span className="pfb-card-title">Feature Request</span>
                <span className="pfb-card-meta">Suggest something new</span>
              </button>

              <button
                type="button"
                className="pfb-card"
                onClick={() => openFlow("bug")}
                disabled={!props.submissionsEnabled}
              >
                <span className="pfb-card-title">Bug Report</span>
                <span className="pfb-card-meta">Something is broken</span>
              </button>

              <button
                type="button"
                className="pfb-card"
                onClick={() => openFlow("general")}
                disabled={!props.submissionsEnabled}
              >
                <span className="pfb-card-title">General</span>
                <span className="pfb-card-meta">Thoughts and suggestions</span>
              </button>

              {props.showRoadmapLink ? (
                <a className="pfb-card" href={props.roadmapUrl} target="_blank" rel="noreferrer">
                  <span className="pfb-card-title">Roadmap</span>
                  <span className="pfb-card-meta">Open product roadmap</span>
                </a>
              ) : null}
              {!props.submissionsEnabled ? (
                <p className="pfb-selection-note">Feedback submissions are currently closed.</p>
              ) : null}
            </div>
          ) : status === "success" ? (
            <div className="pfb-thankyou" role="status" aria-live="polite">
              <div className="pfb-thankyou-icon" aria-hidden="true">✓</div>
              <h3 className="pfb-thankyou-title">Thanks for the feedback</h3>
              <p className="pfb-thankyou-text">{props.successMessage}</p>
              <div className="pfb-thankyou-actions">
                <button type="button" className="pfb-btn pfb-btn-secondary" onClick={() => setOpen(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="pfb-btn pfb-btn-primary"
                  onClick={() => {
                    setStatus("idle");
                    setError("");
                    setFormData(getInitialForm(flowType));
                  }}
                >
                  Send another
                </button>
              </div>
            </div>
          ) : (
            <form className="pfb-form" onSubmit={submitFeedback}>
              <label>
                <span className="pfb-label">Title</span>
                <input
                  ref={titleInputRef}
                  className="pfb-input"
                  type="text"
                  required
                  minLength={3}
                  maxLength={140}
                  value={formData.title}
                  onChange={(event) => {
                    setValue("title", event.target.value);
                    searchSimilar(event.target.value);
                  }}
                  placeholder="Short summary"
                />
              </label>

              {(similarRequests.length > 0 || searchLoading) && !upvotedState ? (
                <div className="pfb-similar">
                  <span className="pfb-similar-label">
                    {searchLoading ? "Searching..." : "Similar requests — upvote instead?"}
                  </span>
                  {similarRequests.map((sr) => (
                    <div key={sr.id} className="pfb-similar-item">
                      <button
                        type="button"
                        className="pfb-similar-vote-btn"
                        onClick={() => handleUpvote(sr)}
                        disabled={upvoteLoading}
                        aria-label={`Upvote: ${sr.title}`}
                      >
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{sr.votes}</span>
                      </button>
                      <div className="pfb-similar-info">
                        <span className="pfb-similar-title">{sr.title}</span>
                        <span className={`pfb-similar-status pfb-status-${sr.status.toLowerCase().replace(/\s+/g, "-")}`}>
                          {sr.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {similarRequests.length > 0 ? (
                    <div className="pfb-similar-divider">
                      <span>or submit new feedback below</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {upvotedState ? (
                <div className="pfb-upvoted-thankyou">
                  <div className="pfb-upvoted-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="pfb-upvoted-msg">
                    <strong>Upvoted!</strong> &ldquo;{upvotedState.request.title}&rdquo; now has {upvotedState.newVotes} {upvotedState.newVotes === 1 ? "vote" : "votes"}.
                  </div>
                  <textarea
                    className="pfb-textarea pfb-upvote-comment"
                    placeholder="Want to add more details? (optional)"
                    value={upvoteComment}
                    onChange={(e) => setUpvoteComment(e.target.value)}
                    maxLength={4000}
                  />
                  <div className="pfb-upvoted-actions">
                    <button
                      type="button"
                      className="pfb-btn pfb-btn-secondary"
                      onClick={() => {
                        setUpvotedState(null);
                        setUpvoteComment("");
                        setStatus("success");
                      }}
                    >
                      Done
                    </button>
                    {upvoteComment.trim() ? (
                      <button
                        type="button"
                        className="pfb-btn pfb-btn-primary"
                        onClick={handleUpvoteCommentSubmit}
                        disabled={upvoteLoading}
                      >
                        {upvoteLoading ? "Sending..." : "Add comment"}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {!upvotedState ? (
                <>
                  <label>
                    <span className="pfb-label">Description</span>
                    <textarea
                      className="pfb-textarea"
                      required
                      minLength={10}
                      maxLength={4000}
                      value={formData.message}
                      onChange={(event) => setValue("message", event.target.value)}
                      placeholder="Tell us what happened or what should improve"
                    />
                  </label>

                  {formData.screenshotUrl ? (
                    <div className="pfb-file-preview-wrap">
                      <img src={formData.screenshotUrl} alt="Attachment preview" className="pfb-file-preview" />
                      <button type="button" className="pfb-file-preview-remove" aria-label="Remove image" onClick={clearScreenshot}>&times;</button>
                    </div>
                  ) : null}

                  {authNotice ? (
                    <div className="pfb-info" role="status">
                      {authNotice}
                    </div>
                  ) : null}

                  {status === "error" && error ? (
                    <div className="pfb-error" role="alert">
                      {error}
                    </div>
                  ) : null}

                  <div className="pfb-actions">
                    <div className="pfb-actions-left">
                      <label className="pfb-small-btn pfb-upload-btn" aria-label="Upload image" title="Upload image">
                        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M10 13.333V5M10 5 6.667 8.333M10 5l3.333 3.333" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4.167 13.333v1.25c0 .92.746 1.667 1.666 1.667h8.334c.92 0 1.666-.746 1.666-1.667v-1.25" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                        </svg>
                        <input type="file" accept="image/*" onChange={handleFileChange} />
                      </label>
                      <button
                        type="button"
                        className="pfb-small-btn"
                        onClick={handleTakeScreenshot}
                        aria-label="Take screenshot"
                        title="Take screenshot"
                      >
                        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M6.667 6.25 7.6 4.817A.833.833 0 0 1 8.3 4.444h3.4a.833.833 0 0 1 .7.373l.933 1.433h1.834c.92 0 1.666.747 1.666 1.667v5.833c0 .92-.746 1.667-1.666 1.667H4.833a1.667 1.667 0 0 1-1.666-1.667V7.917c0-.92.746-1.667 1.666-1.667h1.834Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                          <circle cx="10" cy="10.833" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
                        </svg>
                      </button>
                    </div>

                    <div className="pfb-actions-right">
                      <button
                        type="button"
                        className="pfb-btn pfb-btn-secondary"
                        onClick={() => {
                          setFlowType("selection");
                          setStatus("idle");
                          setError("");
                          setAuthNotice("");
                          setSimilarRequests([]);
                        }}
                        disabled={status === "loading"}
                      >
                        Back
                      </button>
                      <button type="submit" className="pfb-btn pfb-btn-primary" disabled={status === "loading"}>
                        {status === "loading" ? "Sending..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </form>
          )}

          {!props.hideBranding ? (
            <footer className="pfb-branding">Powered by <strong>Peeqback</strong></footer>
          ) : null}
        </section>
      ) : null}

      <button
        type="button"
        className={`pfb-trigger ${props.buttonStyle === "icon-only" ? "pfb-trigger-icon-only" : ""} ${open ? "pfb-trigger-open" : ""}`}
        style={{ backgroundColor: props.brandColor }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        aria-label={props.buttonStyle === "icon-only" ? props.buttonText : undefined}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          setStatus("idle");
          setError("");
          setAuthNotice("");
          setPendingAutoSubmit(false);
          setShowAuthGate(false);
          setFlowType("selection");
          setFormData(getInitialForm("selection"));
          setOpen(true);
        }}
      >
        <span className="pfb-trigger-icon-slot" aria-hidden="true">
          <span className="pfb-trigger-icon">
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.167 4.792h11.666c.46 0 .834.373.834.833v6.25a.833.833 0 0 1-.834.833H9.086l-3.2 2.133c-.555.37-1.303-.028-1.303-.695v-1.438h-.416a.833.833 0 0 1-.834-.833v-6.25c0-.46.373-.833.834-.833Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.083 8.125h5.834M7.083 10h3.75"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <img src={widgetLogoUrl} alt="" className="pfb-trigger-logo" />
        </span>
        {props.buttonStyle !== "icon-only" ? <span>{props.buttonText}</span> : null}
      </button>
    </div>
  );
}
